import React from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
import { Box, Typography } from '@telus-uds/components-web';

import {
  Thead,
  Td,
  Tr,
  Th
} from '../orderDetailsSection/ratePlans/components/css';
import getFormattedPricing from '../orderDetailsSection/utils';

/**
 * Order-summary section for the products a rep completed through an
 * `IoTPopupButton`. Mirrors `SummaryOrderNow`: products are grouped by their
 * source table title, and each group renders a heading + table with a Total
 * row. Rendered from the order's aggregated `iotPopupProducts` list.
 */
const SummaryIotPopupProducts = ({ products }) => {
  const list = Array.isArray(products) ? products : [];
  if (list.length === 0) return null;

  const groups = Object.entries(
    list.reduce((acc, product) => {
      const groupName =
        _.get(product, 'sourceTableTitle', '') ||
        _.get(product, 'sourceTable', '') ||
        _.get(product, 'rateplanType', '');
      if (!acc[groupName]) acc[groupName] = [];
      acc[groupName].push(product);
      return acc;
    }, {})
  );

  return (
    <>
      {groups.map(([groupName, groupProducts]) => {
        const monthlyTotal = groupProducts.reduce(
          (sum, product) => sum + (_.get(product, 'monthly', 0) || 0),
          0
        );
        const oneTimeTotal = groupProducts.reduce(
          (sum, product) => sum + (_.get(product, 'oneTime', 0) || 0),
          0
        );
        // Only show the Term column when at least one product in this group has
        // a term; term-less tables omit it entirely.
        const hasTerm = groupProducts.some(product => _.get(product, 'term'));
        return (
          <Box key={groupName || 'ungrouped'}>
            <Typography heading="h4" variant={{ size: 'h4' }} block>{groupName}</Typography>
            <table>
              <Thead>
                <Tr>
                  <Th>Product</Th>
                  {hasTerm && <Th>Term</Th>}
                  <Th>Quantity</Th>
                  <Th>Monthly</Th>
                  <Th>One-time</Th>
                </Tr>
              </Thead>
              <tbody>
                {groupProducts.map(product => (
                  <tr key={product.key}>
                    <Td style={{ background: '#f7f7f7', fontWeight: '700' }}>
                      {_.get(product, 'label', '')}
                    </Td>
                    {hasTerm && <Td>{_.get(product, 'term', '') || '—'}</Td>}
                    <Td>{_.get(product, 'quantity', 0)}</Td>
                    <Td>{getFormattedPricing(_.get(product, 'monthly', 0))}</Td>
                    <Td>{getFormattedPricing(_.get(product, 'oneTime', 0))}</Td>
                  </tr>
                ))}
                <Tr style={{ background: '#f4f4f4' }}>
                  <Td colSpan={hasTerm ? 3 : 2}>
                    <Typography variant={{ bold: true }}>Total</Typography>
                  </Td>
                  <Td>
                    <Typography variant={{ bold: true }}>{getFormattedPricing(monthlyTotal)}</Typography>
                  </Td>
                  <Td>
                    <Typography variant={{ bold: true }}>{getFormattedPricing(oneTimeTotal)}</Typography>
                  </Td>
                </Tr>
              </tbody>
            </table>
          </Box>
        );
      })}
    </>
  );
};

SummaryIotPopupProducts.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({}))
};

SummaryIotPopupProducts.defaultProps = {
  products: []
};

export default SummaryIotPopupProducts;
